import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  SupportItemResponse,
  GetResourcesRequest,
  StatusMessageResponse,
  GetFrequentAskedQuestionsRequest,
  GetSolutionsRequest,
  AddIssueRequest,
  UpdateIssueRequest,
  IssueResponse,
  UserDataResponse,
} from '../models/support.dto';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SupportApiService {
  private baseUrl = environment.supportApiUrl;

  constructor(private http: HttpClient) {}

  private httpOptions = {
    withCredentials: true,
  };

  getUserData(): Observable<UserDataResponse> {
    return this.http.get<UserDataResponse>(
      `${this.baseUrl}/get_user_data`,
      this.httpOptions
    );
  }

  getResources(
    request: GetResourcesRequest
  ): Observable<SupportItemResponse[]> {
    return this.http.post<SupportItemResponse[]>(
      `${this.baseUrl}/get_resources`,
      request,
      this.httpOptions
    );
  }

  getFrequentAskedQuestions(
    request: GetFrequentAskedQuestionsRequest
  ): Observable<SupportItemResponse[]> {
    return this.http.post<SupportItemResponse[]>(
      `${this.baseUrl}/get_frequent_asked_questions`,
      request,
      this.httpOptions
    );
  }

  getStatusMessage(): Observable<StatusMessageResponse> {
    return this.http.post<StatusMessageResponse>(
      `${this.baseUrl}/get_status_message`,
      {},
      this.httpOptions
    );
  }

  getSolutions(
    request: GetSolutionsRequest
  ): Observable<SupportItemResponse[]> {
    return this.http.post<SupportItemResponse[]>(
      `${this.baseUrl}/get_solutions`,
      request,
      this.httpOptions
    );
  }

  addIssue(request: AddIssueRequest): Observable<IssueResponse> {
    return this.http.post<IssueResponse>(
      `${this.baseUrl}/add_issue`,
      request,
      this.httpOptions
    );
  }

  getIssues(): Observable<SupportItemResponse[]> {
    return this.http.get<SupportItemResponse[]>(
      `${this.baseUrl}/get_issues`,
      this.httpOptions
    );
  }

  getIssue(id: number): Observable<SupportItemResponse> {
    return this.http
      .get<SupportItemResponse>(
        `${this.baseUrl}/get_issue/${id}`,
        this.httpOptions
      )
      .pipe(
        map((res) => {
          res.notes = res.notes?.map((note) => {
            note.date = new Date(note.date);
            return note;
          });
          return res;
        })
      );
  }

  updateIssue(
    issue_id: string,
    request: UpdateIssueRequest
  ): Observable<boolean> {
    return this.http
      .patch<SupportItemResponse>(
        `${this.baseUrl}/${issue_id}/update_issue`,
        request,
        { observe: 'response', ...this.httpOptions }
      )
      .pipe(
        map(
          (response: HttpResponse<SupportItemResponse>) =>
            response.status === 200
        )
      );
  }

  markCorrectSolution(issue_id: string | number): Observable<boolean> {
    return this.http
      .post<any>(
        `${this.baseUrl}/${issue_id}/mark_correct_solution`,
        {},
        {
          observe: 'response',
          ...this.httpOptions,
        }
      )
      .pipe(map((response: HttpResponse<any>) => response.status === 200));
  }
}
